import React, {useMemo, useState} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
  ModalCloseButton,
} from '@chakra-ui/react';
import {AnalystWizard} from './AnalystWizard';
import {useResetAgentBuilder} from '../hooks/useResetAgentBuilder';
import {AgentBuilderContent} from '../AgentBuilderModal';
import {ObjectId} from 'bson';
import {SegmentationWizard} from './SegmentationWizard';
import {useAgent} from '../../../../hooks/api/metadata';
import {AgentType} from '../../../../shared/agent';
import {AgentBuilderStep} from '../AgentBuilderNav.state';
import {TeamSizeWizard} from './TeamSizeWizard';

export type AgentWizard = 'analyst' | 'segmentation' | 'teamSize';

export const AgentWizardModal = ({
  isOpen,
  onClose,
  wizardType,
}: {
  isOpen: boolean;
  onClose: () => void;
  wizardType: AgentWizard;
}) => {
  const resetAgentBuilder = useResetAgentBuilder();

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={resetAgentBuilder}
      size="full"
    >
      <ModalOverlay />
      <ModalContent>
        <AgentWizardContent wizardType={wizardType} />
      </ModalContent>
    </Modal>
  );
};

const initialStepForAgentType = {
  analyst: 'editAnalystField',
  legacyResearch: 'editLegacyResearchField',
  legacyResearchAndAnalyst: 'editLegacyResearchField',
  legacyTeamSize: 'editLegacyTeamSizeField',
  research: 'editResearchField',
  researchAndAnalyst: 'editResearchField',
  teamSize: 'editTeamSizeField',
} as const satisfies Record<AgentType, AgentBuilderStep>;

const AgentWizardContent = ({wizardType}: {wizardType: AgentWizard}) => {
  const [agentId, setAgentId] = useState<ObjectId | undefined>();
  const agent = useAgent(agentId);

  const wizards = useMemo(
    () =>
      ({
        analyst: <AnalystWizard onContinue={setAgentId} />,
        segmentation: <SegmentationWizard onContinue={setAgentId} />,
        teamSize: <TeamSizeWizard onContinue={setAgentId} />,
      }) satisfies Record<AgentWizard, React.ReactNode>,
    []
  );

  if (agent) {
    return (
      <AgentBuilderContent
        agentId={agent.id}
        initialStep={initialStepForAgentType[agent.type]}
      />
    );
  }

  return (
    <>
      <ModalBody as={Flex} flexDirection="column" p="0">
        {wizards[wizardType]}
      </ModalBody>
      <ModalCloseButton />
    </>
  );
};
